import { Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { OrderState, type OrderChannel } from '@erpgold/shared';
import type { ERPColumnDef } from '@/components/DataTable/ERPTable';
import { createActionColumn } from '@/components/DataTable/ActionColumn';
import { formatVND, formatDateTime } from '@/utils/format';
import type { TenantOrder } from '@/mocks/orders';
import {
  CHANNEL_GROUP_COLOR,
  CHANNEL_GROUP_ICON,
  CHANNEL_GROUP_LABEL,
  ORDER_PAYMENT_LABEL,
  STATUS_GROUP_COLOR,
  STATUS_GROUP_LABEL,
  resolveChannelGroup,
  resolveStatusGroup,
} from './constants';
import { ROUTES } from '@/constants/routes';

const { Text } = Typography;

export const orderColumns: ERPColumnDef<TenantOrder>[] = [
  {
    key: 'order_number',
    title: 'Mã đơn',
    alwaysVisible: true,
    width: 150,
    dataIndex: 'order_number',
    sorter: (a, b) => a.order_number.localeCompare(b.order_number),
    render: (v: string, r) => <Link to={ROUTES.orders.detail(r._id)}><Text code style={{ fontSize: 12 }}>{v}</Text></Link>,
  },
  {
    key: 'channel',
    title: 'Kênh',
    hideable: true,
    width: 120,
    dataIndex: 'channel',
    render: (v: OrderChannel) => {
      const g = resolveChannelGroup(v);
      return (
        <Tag color={CHANNEL_GROUP_COLOR[g]}>
          {CHANNEL_GROUP_ICON[g]} {CHANNEL_GROUP_LABEL[g]}
        </Tag>
      );
    },
  },
  {
    key: 'customer',
    title: 'Khách hàng',
    hideable: true,
    renameable: true,
    width: 180,
    render: (_: unknown, r) => (
      <div>
        <Text strong style={{ fontSize: 13 }}>{r.customer?.name ?? '-'}</Text>
        {r.customer?.phone && (
          <>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>{r.customer.phone}</Text>
          </>
        )}
      </div>
    ),
  },
  {
    key: 'item_count',
    title: 'Sản phẩm',
    hideable: true,
    width: 90,
    hideOnMobile: true,
    render: (_: unknown, r) => <Text>{r.items?.length ?? 0} mặt hàng</Text>,
  },
  {
    key: 'total_amount',
    title: 'Tổng tiền',
    hideable: true,
    width: 150,
    dataIndex: 'total_amount',
    sorter: (a, b) => a.total_amount - b.total_amount,
    render: (v: number) => <Text style={{ color: '#a4193d', fontWeight: 600 }}>{formatVND(v)}</Text>,
  },
  {
    key: 'payment_method',
    title: 'Thanh toán',
    hideable: true,
    width: 120,
    hideOnMobile: true,
    dataIndex: 'payment_method',
    render: (v: string | undefined) => <Text>{v ? (ORDER_PAYMENT_LABEL[v] ?? v) : '—'}</Text>,
  },
  {
    key: 'state',
    title: 'Trạng thái',
    hideable: true,
    width: 130,
    dataIndex: 'state',
    render: (v: OrderState) => {
      const g = resolveStatusGroup(v);
      return <Tag color={STATUS_GROUP_COLOR[g]}>{STATUS_GROUP_LABEL[g]}</Tag>;
    },
  },
  {
    key: 'cashier',
    title: 'Nhân viên',
    hideable: true,
    width: 130,
    hideOnMobile: true,
    render: (_: unknown, r) => <Text>{r.cashier?.name ?? '—'}</Text>,
  },
  {
    key: 'created_at',
    title: 'Thời gian',
    hideable: true,
    width: 150,
    hideOnMobile: true,
    dataIndex: 'created_at',
    sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    render: (v: string) => <Text style={{ fontSize: 12 }}>{formatDateTime(v)}</Text>,
  },
];

export function buildOrderActionColumn(onAction: (key: string, row: TenantOrder) => void) {
  return createActionColumn<TenantOrder>({
    items: (row) => [
      { key: 'view', label: 'Xem chi tiết' },
      { type: 'divider' as const },
      // Print options — mỗi kind 1 menu item, default tenant-side quyết định.
      // Key prefix 'print:' để parent handler parse.
      { key: 'print:receipt', label: '🧾 In hoá đơn POS' },
      { key: 'print:invoice', label: '🧮 In hoá đơn VAT' },
      { key: 'print:gold_guarantee', label: '📜 In giấy đảm bảo vàng' },
      { type: 'divider' as const },
      {
        key: 'cancel',
        label: 'Huỷ đơn',
        danger: true,
        disabled: row.state === OrderState.COMPLETED || row.state === OrderState.CANCELED,
      },
    ],
    onAction,
  });
}
